import { useState } from 'react'

import { useMultiSelect } from '@coinbase/cds-common/select/useMultiSelect'
import { ThemeProvider } from '@coinbase/cds-web'
import { Select as MultiSelect } from '@coinbase/cds-web/alpha/select/index'
import { defaultTheme } from '@coinbase/cds-web/themes/defaultTheme'
import { useForm, Controller } from 'react-hook-form'

import {
  formMessages,
  defaultValues,
  leadGenData,
  contactFormUrl,
} from ':cbam/components/ContactForm/FieldData'
import type { ContactFormProps } from ':cbam/components/ContactForm/types'
import { useCookie } from '@cb/cookie-manager'
import { Checkbox } from '@cbhq/cds-web/controls/Checkbox'
import { Select } from '@cbhq/cds-web/controls/Select'
import { SelectOption } from '@cbhq/cds-web/controls/SelectOption'
import { TextInput } from '@cbhq/cds-web/controls/TextInput'
import { useToast } from '@cbhq/cds-web/overlays/useToast'
import { Div, Text, Button, useLogger } from '@cbhq/cdx-components'
import { useContentfulMicrocopy } from '@cbhq/cdx-contentful'

export const ContactForm = ({
  title,
  buttonText,
  action,
  successMessage,
  errorMessage,
  disclaimer,
}: ContactFormProps) => {
  const formAction = action || contactFormUrl

  const {
    register,
    handleSubmit,
    control,
    formState,
    setValue,
    getValues,
    reset,
  } = useForm<any>({
    defaultValues,
  })

  const [disclaimerConsent, setDisclaimerConsent] = useState(false)
  const [emailConsent, setEmailConsent] = useState(false)

  const [locationError, setLocationError] = useState(false)
  const [hasOptedOutOfFundsError, setHasOptedOutOfFundsError] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [utms = {}] = useCookie('utm')
  const microcopies = useContentfulMicrocopy()
  const { logError } = useLogger()
  const toast = useToast()

  const {
    firstNameLabel,
    firstNamePlaceholder,
    lastNameLabel,
    lastNamePlaceholder,
    companyLabel,
    companyPlaceholder,
    locationLabel,
    locationPlaceholder,
  } = microcopies.cloud || {}

  const {
    value: investorTypeValue,
    onChange: investorTypeOnChange,
    resetSelection: resetInvestorTypeSelection,
  } = useMultiSelect({
    initialValue: [],
  })
  const {
    value: currentInvestmentStrategyValue,
    onChange: currentInvestmentStrategyOnChange,
    resetSelection: resetCurrentInvestmentStrategySelection,
  } = useMultiSelect({
    initialValue: [],
  })
  const {
    value: interestedFundsValue,
    onChange: interestedFundsOnChange,
    resetSelection: resetInterestedFundsSelection,
  } = useMultiSelect({
    initialValue: [],
  })
  const {
    value: stayConnectedMethodValue,
    onChange: stayConnectedMethodOnChange,
    resetSelection: resetStayConnectedMethodSelection,
  } = useMultiSelect({
    initialValue: [],
  })

  function resetAllMultiSelectSelections() {
    resetInvestorTypeSelection()
    resetCurrentInvestmentStrategySelection()
    resetInterestedFundsSelection()
    resetStayConnectedMethodSelection()
  }

  const { isSubmitting, errors } = formState

  const onSubmit = async (data: any) => {
    //getting field names to match production and development sandbox
    const {
      email,
      firstName,
      lastName,
      company,
      location,
      phone,
      description,
      governmentEntity,
      interestInProduct,
      hasOptedOutOfFunds,
    } = data
    const formPayload = JSON.stringify({
      email,
      firstName,
      lastName,
      company: company === '' ? '[not provided]' : company,
      location,
      phone,
      description,
      governmentEntity,
      interestInProduct,
      leadSourceV2: 'LEAD_SOURCE_WEB_MARKETING_WEBSITE',
      newsletterSignup: false,
      isCbamLead: true,
      investorType: investorTypeValue,
      currentInvestmentStrategy: currentInvestmentStrategyValue,
      interestedFunds: interestedFundsValue,
      stayConnectedMethod: stayConnectedMethodValue,
      hasOptedOutOfFund:
        leadGenData.hasOptedOutOfFundsMap[
          hasOptedOutOfFunds as keyof typeof leadGenData.hasOptedOutOfFundsMap
        ],
      ...utms,
    })
    try {
      const result = await fetch(formAction, {
        method: 'POST',
        //mode: 'no-cors',
        headers: {
          Accept: '*/*',
          'Content-Type': 'application/json',
          Connection: 'keep-alive',
          'Accept-Encoding': 'gzip, deflate, br',
        },
        body: formPayload,
      })
      if (result.ok) {
        toast.show(
          successMessage || formMessages.submitSuccessMessage.defaultMessage,
        )
        setDisclaimerConsent(false)
        setEmailConsent(false)

        reset()
        resetAllMultiSelectSelections()
      }
    } catch (error) {
      toast.show(
        errorMessage || formMessages.submitFailureMessage.defaultMessage,
      )
      setDisclaimerConsent(false)
      setEmailConsent(false)
      const err = new Error('Cloud Lead gen Error Posting data to Salesforce')
      err.cause = error
      logError(err.message, err)
      // TODO: Should we be using bugsnag here
    }
  }

  const onError = (error: any) => {
    error.location && setLocationError(true)
    error.hasOptedOutOfFunds && setHasOptedOutOfFundsError(true)
  }

  return (
    <ThemeProvider theme={defaultTheme} activeColorScheme="light">
      <Div
        flexDirection={'column'}
        alignItems="center"
        spacingVertical={[1, 5]}
        spacingHorizontal={[1, 5]}
      >
        <Div
          gap={5}
          width="100%"
          maxWidth="1200px"
          spacingVertical={5}
          spacingHorizontal={5}
          bordered
          background="background"
          borderRadius="rounded"
          flexDirection="column"
        >
          <Div width="100%" maxWidth="1052" flexDirection="column">
            <Div width="100%" spacingBottom={2}>
              <Text variant="display2" as="h2">
                {title}
              </Text>
            </Div>
            <Div
              gap={{ sm: 0, md: 3 }}
              flexDirection={{ sm: 'column', md: 'row' }}
            >
              <Div width="100%" spacingBottom={4}>
                <TextInput
                  label={
                    firstNameLabel || formMessages.firstNameLabel.defaultMessage
                  }
                  placeholder={
                    firstNamePlaceholder ||
                    formMessages.firstNamePlaceholder.defaultMessage
                  }
                  {...register(leadGenData.firstName.name, {
                    required: true,
                    minLength: 3,
                  })}
                  helperText={
                    errors.firstName &&
                    formMessages.firstNameError.defaultMessage
                  }
                  variant={errors.firstName && 'negative'}
                />
              </Div>

              <Div width="100%" spacingBottom={4}>
                <TextInput
                  label={
                    lastNameLabel || formMessages.lastNameLabel.defaultMessage
                  }
                  placeholder={
                    lastNamePlaceholder ||
                    formMessages.lastNamePlaceholder.defaultMessage
                  }
                  {...register(leadGenData.lastName.name, {
                    required: true,
                    minLength: 3,
                  })}
                  helperText={
                    errors.lastName && formMessages.lastNameError.defaultMessage
                  }
                  variant={errors.lastName && 'negative'}
                />
              </Div>
            </Div>

            <Div
              gap={{ sm: 0, md: 3 }}
              flexDirection={{ sm: 'column', md: 'row' }}
            >
              <Div width="100%" spacingBottom={4}>
                <TextInput
                  label={formMessages.emailLabel.defaultMessage}
                  placeholder={formMessages.emailPlaceholder.defaultMessage}
                  {...register(leadGenData.email.name, {
                    required: true,
                    pattern: /^\S+@\S+$/i,
                  })}
                  type="email"
                  helperText={
                    errors.email && formMessages.emailError.defaultMessage
                  }
                  variant={errors.email && 'negative'}
                  key={`${leadGenData.email.name}-inputkey`}
                />
              </Div>

              <Div width="100%" spacingBottom={4}>
                <TextInput
                  label={formMessages.phoneLabel.defaultMessage}
                  placeholder={formMessages.phonePlaceholder.defaultMessage}
                  {...register(leadGenData.phone.name, {
                    required: false,
                  })}
                />
              </Div>
            </Div>

            <Div
              gap={{ sm: 0, md: 3 }}
              flexDirection={{ sm: 'column', md: 'row' }}
            >
              <Div width="100%" spacingBottom={4}>
                <TextInput
                  label={
                    companyLabel || formMessages.companyNameLabel.defaultMessage
                  }
                  placeholder={
                    companyPlaceholder ||
                    formMessages.companyNamePlaceholder.defaultMessage
                  }
                  {...register(leadGenData.companyName.name, {
                    required: true,
                  })}
                  helperText={
                    errors.company &&
                    formMessages.companyNameError.defaultMessage
                  }
                  variant={errors.company && 'negative'}
                  key={`${leadGenData.companyName.name}-inputkey`}
                />
              </Div>
              <Div width="100%" spacingBottom={4}>
                <Controller
                  control={control}
                  name={leadGenData.location.name}
                  rules={{ required: true }}
                  render={({ field: { onChange } }) => {
                    return (
                      <Select
                        onChange={(value: string) => {
                          onChange(value)
                          setLocationError(false)
                        }}
                        label={
                          locationLabel ||
                          formMessages.locationLabel.defaultMessage
                        }
                        placeholder={
                          locationPlaceholder ||
                          formMessages.locationPlaceholder.defaultMessage
                        }
                        value={getValues(leadGenData.location.name)}
                        variant={locationError ? 'negative' : undefined}
                        helperText={
                          locationError
                            ? formMessages.locationError.defaultMessage
                            : undefined
                        }
                      >
                        {leadGenData.location.optionValues.map((option) => (
                          <SelectOption
                            value={option.title}
                            key={option.value}
                            title={option.title}
                          />
                        ))}
                      </Select>
                    )
                  }}
                />
              </Div>
            </Div>

            <Div
              gap={{ sm: 0, md: 3 }}
              flexDirection={{ sm: 'column', md: 'row' }}
            >
              <Div width="100%" spacingBottom={4}>
                <MultiSelect
                  styles={{ root: { width: '100%' } }}
                  type="multi"
                  onChange={investorTypeOnChange}
                  label={formMessages.investorTypeLabel.defaultMessage}
                  placeholder={
                    formMessages.investorTypePlaceholder.defaultMessage
                  }
                  value={investorTypeValue || []}
                  variant={errors.investorType && 'negative'}
                  helperText={
                    errors.investorType &&
                    formMessages.investorTypeError.defaultMessage
                  }
                  options={leadGenData.investorType.optionValues.map(
                    (option) => ({
                      value: option.value,
                      label: option.title,
                    }),
                  )}
                />
              </Div>

              <Div width="100%" spacingBottom={4}>
                <MultiSelect
                  styles={{ root: { width: '100%' } }}
                  type="multi"
                  onChange={currentInvestmentStrategyOnChange}
                  label={
                    formMessages.currentInvestmentStrategyLabel.defaultMessage
                  }
                  placeholder={
                    formMessages.currentInvestmentStrategyPlaceholder
                      .defaultMessage
                  }
                  value={currentInvestmentStrategyValue || []}
                  variant={errors.currentInvestmentStrategy && 'negative'}
                  helperText={
                    errors.currentInvestmentStrategy &&
                    formMessages.currentInvestmentStrategyError.defaultMessage
                  }
                  options={leadGenData.currentInvestmentStrategy.optionValues.map(
                    (option) => ({
                      value: option.value,
                      label: option.title,
                    }),
                  )}
                />
              </Div>
            </Div>

            <Div
              gap={{ sm: 0, md: 3 }}
              flexDirection={{ sm: 'column', md: 'row' }}
            >
              <Div width="100%" spacingBottom={4}>
                <MultiSelect
                  styles={{ root: { width: '100%' } }}
                  type="multi"
                  onChange={interestedFundsOnChange}
                  label={formMessages.interestedFundsLabel.defaultMessage}
                  placeholder={
                    formMessages.interestedFundsPlaceholder.defaultMessage
                  }
                  value={interestedFundsValue || []}
                  variant={errors.interestedFunds ? 'negative' : undefined}
                  helperText={
                    errors.interestedFunds &&
                    formMessages.interestedFundsError.defaultMessage
                  }
                  options={leadGenData.interestedFunds.optionValues.map(
                    (option) => ({
                      value: option.value,
                      label: option.title,
                    }),
                  )}
                />
              </Div>

              <Div width="100%" spacingBottom={4}>
                <MultiSelect
                  styles={{ root: { width: '100%' } }}
                  type="multi"
                  onChange={stayConnectedMethodOnChange}
                  label={formMessages.stayConnectedLabel.defaultMessage}
                  placeholder={
                    formMessages.stayConnectedPlaceholder.defaultMessage
                  }
                  value={stayConnectedMethodValue || []}
                  variant={errors.stayConnectedMethod ? 'negative' : undefined}
                  helperText={
                    errors.stayConnectedMethod &&
                    formMessages.stayConnectedError.defaultMessage
                  }
                  options={leadGenData.stayConnectedMethod.optionValues.map(
                    (option) => ({
                      value: option.value,
                      label: option.title,
                    }),
                  )}
                />
              </Div>
            </Div>

            <Div width="100%" spacingBottom={4}>
              <TextInput
                label={formMessages.descriptionLabel.defaultMessage}
                placeholder={formMessages.descriptionPlaceholder.defaultMessage}
                {...register(leadGenData.description.name, {
                  required: false,
                  minLength: 10,
                })}
                helperText={
                  errors.description &&
                  formMessages.descriptionError.defaultMessage
                }
                variant={errors.description && 'negative'}
                maxLength={500}
              />
            </Div>

            <Div width="100%" spacingBottom={4}>
              <Controller
                control={control}
                name={leadGenData.hasOptedOutOfFunds.name}
                rules={{ required: true }}
                render={({ field: { onChange } }) => {
                  return (
                    <Select
                      onChange={(value: string) => {
                        onChange(value)
                        setHasOptedOutOfFundsError(false)
                      }}
                      label={
                        formMessages.hasOptedOutOfFundsLabel.defaultMessage
                      }
                      placeholder={
                        formMessages.hasOptedOutOfFundsPlaceholder
                          .defaultMessage
                      }
                      value={getValues(leadGenData.hasOptedOutOfFunds.name)}
                      variant={hasOptedOutOfFundsError ? 'negative' : undefined}
                      helperText={
                        hasOptedOutOfFundsError
                          ? formMessages.hasOptedOutOfFundsError.defaultMessage
                          : undefined
                      }
                    >
                      {leadGenData.hasOptedOutOfFunds.optionValues.map(
                        (option) => (
                          <SelectOption
                            value={option.value}
                            key={option.value}
                            title={option.title}
                          />
                        ),
                      )}
                    </Select>
                  )
                }}
              />
            </Div>

            <Div width="100%" spacingBottom={4}>
              <Checkbox
                value="emailConsent"
                onChange={() => setEmailConsent(!emailConsent)}
                checked={emailConsent}
              >
                <Text
                  variant="label1"
                  as="p"
                  spacingVertical={0.5}
                  css={{
                    whiteSpace: 'pre-line',
                  }}
                >
                  <span>{formMessages.emailConsentMessage.defaultMessage}</span>
                </Text>
              </Checkbox>
            </Div>

            <Div width="100%" spacingBottom={4}>
              <Checkbox
                value="disclaimerConsent"
                onChange={() => setDisclaimerConsent(!disclaimerConsent)}
                checked={disclaimerConsent}
              >
                <Text
                  variant="label1"
                  as="p"
                  spacingVertical={0.5}
                  css={{
                    whiteSpace: 'pre-line',
                  }}
                >
                  <span>
                    {isExpanded
                      ? disclaimer ||
                        formMessages.acceptTermsOfUse.defaultMessage
                      : `${
                          (disclaimer && disclaimer?.slice(0, 809)) ||
                          formMessages.acceptTermsOfUse.defaultMessage.slice(
                            0,
                            809,
                          )
                        }... `}

                    <button
                      onClick={() => setIsExpanded(!isExpanded)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: 'var(--primary)',
                      }}
                    >
                      {isExpanded ? 'Read Less' : 'Read More'}
                    </button>
                  </span>
                </Text>
              </Checkbox>
            </Div>
          </Div>
          <Div>
            <Div width={['100%', '320px']}>
              <Button
                onPress={handleSubmit(onSubmit, onError)}
                variant={'primary'}
                disabled={isSubmitting || !disclaimerConsent || !emailConsent}
              >
                {buttonText}
              </Button>
            </Div>
          </Div>
        </Div>
      </Div>
    </ThemeProvider>
  )
}
